import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { AuthProvider } from "./auth/AuthContext";
import { AuthGuard, GuestGuard } from "./auth/AuthGuard";
import { AppShell } from "./features/_layout/AppShell";
import { LoginPage } from "./features/auth/LoginPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { InvitationAcceptPage } from "./features/auth/InvitationAcceptPage";
import { ImpersonatePage } from "./auth/ImpersonatePage";
import { AccountBlockedPage } from "./features/auth/AccountBlockedPage";
import { SignatureContratPage } from "./features/auth/SignatureContratPage";
import { MandatGatePage } from "./features/abonnement/MandatGatePage";
import { DemoRequiseGatePage } from "./features/auth/DemoRequiseGatePage";
import { RequireActivity } from "./auth/activityGating";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { PageSpinner } from "./ui/Spinner";

/**
 * L · Lazy-loading des features lourdes pour réduire le bundle initial.
 * Le login/reset/invite/dashboard reste eager (chemin chaud).
 * Toutes les autres pages sont chargées à la demande.
 */
const MonComptePage = lazy(() =>
  import("./features/account/MonComptePage").then((m) => ({ default: m.MonComptePage })),
);
const UsersPage = lazy(() =>
  import("./features/users/UsersPage").then((m) => ({ default: m.UsersPage })),
);
const EtablissementsPage = lazy(() =>
  import("./features/etablissements/EtablissementsPage").then((m) => ({ default: m.EtablissementsPage })),
);
const SitesPage = lazy(() =>
  import("./features/sites/SitesPage").then((m) => ({ default: m.SitesPage })),
);
const AgentsPage = lazy(() =>
  import("./features/agents/AgentsPage").then((m) => ({ default: m.AgentsPage })),
);
const AgentDetailPage = lazy(() =>
  import("./features/agents/AgentDetailPage").then((m) => ({ default: m.AgentDetailPage })),
);
const CongesPage = lazy(() =>
  import("./features/conges/CongesPage").then((m) => ({ default: m.CongesPage })),
);
const PrepaiePage = lazy(() =>
  import("./features/prepaie/PrepaiePage").then((m) => ({ default: m.PrepaiePage })),
);
const CynophilePage = lazy(() =>
  import("./features/cynophile/CynophilePage").then((m) => ({ default: m.CynophilePage })),
);
const SsiapPage = lazy(() =>
  import("./features/ssiap/SsiapPage").then((m) => ({ default: m.SsiapPage })),
);
const TelesurveillancePage = lazy(() =>
  import("./features/telesurveillance/TelesurveillancePage").then((m) => ({ default: m.TelesurveillancePage })),
);
const EvenementielPage = lazy(() =>
  import("./features/evenementiel/EvenementielPage").then((m) => ({ default: m.EvenementielPage })),
);
const SousTraitancePage = lazy(() =>
  import("./features/sous-traitance/SousTraitancePage").then((m) => ({ default: m.SousTraitancePage })),
);
const MaterielPage = lazy(() =>
  import("./features/materiel/MaterielPage").then((m) => ({ default: m.MaterielPage })),
);
const ReportingPage = lazy(() =>
  import("./features/reporting/ReportingPage").then((m) => ({ default: m.ReportingPage })),
);
const OpportunitesPage = lazy(() =>
  import("./features/opportunites/OpportunitesPage").then((m) => ({ default: m.OpportunitesPage })),
);
const MobilePage = lazy(() =>
  import("./features/mobile/MobilePage").then((m) => ({ default: m.MobilePage })),
);
const PlanningTemplatesPage = lazy(() =>
  import("./features/planning-templates/PlanningTemplatesPage").then((m) => ({ default: m.PlanningTemplatesPage })),
);
const MessageriePage = lazy(() =>
  import("./features/messagerie/MessageriePage").then((m) => ({ default: m.MessageriePage })),
);
const PlanningCalendrierPage = lazy(() =>
  import("./features/planning-calendrier/PlanningCalendrierPage").then((m) => ({ default: m.PlanningCalendrierPage })),
);
const RecrutementPage = lazy(() =>
  import("./features/recrutement/RecrutementPage").then((m) => ({ default: m.RecrutementPage })),
);
const CartePage = lazy(() =>
  import("./features/carte/CartePage").then((m) => ({ default: m.CartePage })),
);
const ClientsPage = lazy(() =>
  import("./features/clients/ClientsPage").then((m) => ({ default: m.ClientsPage })),
);
const ContratsPage = lazy(() =>
  import("./features/contrats/ContratsPage").then((m) => ({ default: m.ContratsPage })),
);
const PlanningPage = lazy(() =>
  import("./features/planning/PlanningPage").then((m) => ({ default: m.PlanningPage })),
);
const VacationDetailPage = lazy(() =>
  import("./features/vacations/VacationDetailPage").then((m) => ({ default: m.VacationDetailPage })),
);
const PointagesPage = lazy(() =>
  import("./features/pointages/PointagesPage").then((m) => ({ default: m.PointagesPage })),
);
const MainCourantePage = lazy(() =>
  import("./features/main-courante/MainCourantePage").then((m) => ({ default: m.MainCourantePage })),
);
const RondesPage = lazy(() =>
  import("./features/rondes/RondesPage").then((m) => ({ default: m.RondesPage })),
);
const AlertesPage = lazy(() =>
  import("./features/alertes/AlertesPage").then((m) => ({ default: m.AlertesPage })),
);
const ParametresPage = lazy(() =>
  import("./features/parametres/ParametresPage").then((m) => ({ default: m.ParametresPage })),
);
const CommercialPage = lazy(() =>
  import("./features/commercial/CommercialPage").then((m) => ({ default: m.CommercialPage })),
);
const DevisAcceptPage = lazy(() =>
  import("./features/commercial/DevisAcceptPage").then((m) => ({ default: m.DevisAcceptPage })),
);
const DocumentsPage = lazy(() =>
  import("./features/documents/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
);
const AuditPage = lazy(() =>
  import("./features/audit/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const ImportExportPage = lazy(() =>
  import("./features/import-export/ImportExportPage").then((m) => ({ default: m.ImportExportPage })),
);
const RgpdPage = lazy(() =>
  import("./features/rgpd/RgpdPage").then((m) => ({ default: m.RgpdPage })),
);
const RapportsIncidentPage = lazy(() =>
  import("./features/rapports-incident/RapportsIncidentPage").then((m) => ({ default: m.RapportsIncidentPage })),
);
const WebhooksPage = lazy(() =>
  import("./features/webhooks/WebhooksPage").then((m) => ({ default: m.WebhooksPage })),
);
const AnomaliesPage = lazy(() =>
  import("./features/anomalies/AnomaliesPage").then((m) => ({ default: m.AnomaliesPage })),
);
const AstreintesPage = lazy(() =>
  import("./features/astreintes/AstreintesPage").then((m) => ({ default: m.AstreintesPage })),
);
const ItemsSensiblesPage = lazy(() =>
  import("./features/items-sensibles/ItemsSensiblesPage").then((m) => ({ default: m.ItemsSensiblesPage })),
);
const VisitesControlePage = lazy(() =>
  import("./features/visites-controle/VisitesControlePage").then((m) => ({ default: m.VisitesControlePage })),
);
const AbonnementPage = lazy(() =>
  import("./features/abonnement/AbonnementPage").then((m) => ({ default: m.AbonnementPage })),
);
const ResilierPage = lazy(() =>
  import("./features/abonnement/ResilierPage").then((m) => ({ default: m.ResilierPage })),
);
const BoutiquePage = lazy(() =>
  import("./features/boutique/BoutiquePage").then((m) => ({ default: m.BoutiquePage })),
);
const HelpPage = lazy(() =>
  import("./features/help/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const SupportPage = lazy(() =>
  import("./features/support/SupportPage").then((m) => ({ default: m.SupportPage })),
);

/**
 * Routing platform.
 * - `/login` public (GuestGuard : redirige sur / si déjà connecté)
 * - Tout le reste sous <AuthGuard> + <AppShell> (sidebar + topbar)
 */
export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          }
        />
        <Route path="/reset" element={<ResetPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/compte-bloque" element={<AccountBlockedPage />} />
        <Route path="/signature" element={<SignatureContratPage />} />
        <Route path="/mandat" element={<MandatGatePage />} />
        <Route path="/demo-requise" element={<DemoRequiseGatePage />} />
        <Route
          path="/invitation/:token"
          element={<InvitationAcceptPage />}
        />
        <Route path="/devis/accept/:token" element={<Suspense fallback={<PageSpinner />}><DevisAcceptPage /></Suspense>} />

        <Route
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="/mon-compte" element={<Lazy><MonComptePage /></Lazy>} />
          <Route path="/utilisateurs" element={<Lazy><UsersPage /></Lazy>} />
          <Route path="/etablissements" element={<Lazy><EtablissementsPage /></Lazy>} />
          <Route path="/sites" element={<Lazy><SitesPage /></Lazy>} />
          <Route path="/agents" element={<Lazy><AgentsPage /></Lazy>} />
          <Route path="/agents/:id" element={<Lazy><AgentDetailPage /></Lazy>} />
          <Route path="/conges" element={<Lazy><CongesPage /></Lazy>} />
          <Route path="/prepaie" element={<Lazy><PrepaiePage /></Lazy>} />
          <Route path="/cynophile" element={<RequireActivity activity="cynophile"><Lazy><CynophilePage /></Lazy></RequireActivity>} />
          <Route path="/ssiap" element={<RequireActivity activity="ssiap"><Lazy><SsiapPage /></Lazy></RequireActivity>} />
          <Route path="/telesurveillance" element={<RequireActivity activity="telesurveillance"><Lazy><TelesurveillancePage /></Lazy></RequireActivity>} />
          <Route path="/evenementiel" element={<RequireActivity activity="evenementiel"><Lazy><EvenementielPage /></Lazy></RequireActivity>} />
          <Route path="/sous-traitance" element={<Lazy><SousTraitancePage /></Lazy>} />
          <Route path="/materiel" element={<Lazy><MaterielPage /></Lazy>} />
          <Route path="/reporting" element={<Lazy><ReportingPage /></Lazy>} />
          <Route path="/opportunites" element={<Lazy><OpportunitesPage /></Lazy>} />
          <Route path="/mobile" element={<Lazy><MobilePage /></Lazy>} />
          <Route path="/planning-templates" element={<Lazy><PlanningTemplatesPage /></Lazy>} />
          <Route path="/messagerie" element={<Lazy><MessageriePage /></Lazy>} />
          <Route path="/planning-calendrier" element={<Lazy><PlanningCalendrierPage /></Lazy>} />
          <Route path="/recrutement" element={<Lazy><RecrutementPage /></Lazy>} />
          <Route path="/carte" element={<Lazy><CartePage /></Lazy>} />
          <Route path="/clients" element={<Lazy><ClientsPage /></Lazy>} />
          <Route path="/contrats" element={<Lazy><ContratsPage /></Lazy>} />
          <Route path="/commercial" element={<Lazy><CommercialPage /></Lazy>} />
          <Route path="/planning" element={<Lazy><PlanningPage /></Lazy>} />
          <Route path="/vacations/:id" element={<Lazy><VacationDetailPage /></Lazy>} />
          <Route path="/pointages" element={<Lazy><PointagesPage /></Lazy>} />
          <Route path="/main-courante" element={<Lazy><MainCourantePage /></Lazy>} />
          <Route path="/rondes" element={<Lazy><RondesPage /></Lazy>} />
          <Route path="/alertes" element={<Lazy><AlertesPage /></Lazy>} />
          <Route path="/documents" element={<Lazy><DocumentsPage /></Lazy>} />
          <Route path="/audit" element={<Lazy><AuditPage /></Lazy>} />
          <Route path="/import-export" element={<Lazy><ImportExportPage /></Lazy>} />
          <Route path="/rgpd" element={<Lazy><RgpdPage /></Lazy>} />
          <Route path="/rapports-incident" element={<Lazy><RapportsIncidentPage /></Lazy>} />
          <Route path="/webhooks" element={<Lazy><WebhooksPage /></Lazy>} />
          <Route path="/anomalies" element={<Lazy><AnomaliesPage /></Lazy>} />
          <Route path="/astreintes" element={<Lazy><AstreintesPage /></Lazy>} />
          <Route path="/items-sensibles" element={<Lazy><ItemsSensiblesPage /></Lazy>} />
          <Route path="/visites-controle" element={<Lazy><VisitesControlePage /></Lazy>} />
          <Route path="/abonnement" element={<Lazy><AbonnementPage /></Lazy>} />
          <Route path="/abonnement/resilier" element={<Lazy><ResilierPage /></Lazy>} />
          <Route path="/boutique" element={<Lazy><BoutiquePage /></Lazy>} />
          <Route path="/help" element={<Lazy><HelpPage /></Lazy>} />
          <Route path="/support" element={<Lazy><SupportPage /></Lazy>} />
          <Route path="/parametres" element={<Lazy><ParametresPage /></Lazy>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSpinner />}>{children}</Suspense>;
}
